import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './DashboardPage.css';

export default function DashboardPage() {
  const [members,        setMembers]        = useState([]);
  const [events,         setEvents]         = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingEvents,  setLoadingEvents]  = useState(true);

  // Real-time listeners
  useEffect(() => {
    const unsubMembers = onSnapshot(
      collection(db, 'allowed_users'),
      (snap) => {
        setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoadingMembers(false);
      },
      () => setLoadingMembers(false),
    );

    const unsubEvents = onSnapshot(
      query(collection(db, 'events'), where('status', 'in', ['upcoming', 'soon'])),
      (snap) => {
        setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoadingEvents(false);
      },
      () => setLoadingEvents(false),
    );

    return () => { unsubMembers(); unsubEvents(); };
  }, []);

  // Derived stats
  const active   = members.filter(m => m.status === 'active').length;
  const inactive = members.length - active;

  // Members grouped by district - Kerala chapters only
  const KERALA_DISTRICTS = new Set([
    'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
    'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
    'Malappuram', 'Calicut', 'Wayanad', 'Kannur', 'Kasaragod',
  ]);
  const byDistrict = members.reduce((acc, m) => {
    const d = m.district;
    if (!d || !KERALA_DISTRICTS.has(d)) return acc;
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});
  const topDistricts = Object.entries(byDistrict)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const maxCount = topDistricts[0]?.[1] || 1;

  // Recently added - sort by joined_on descending, take 5
  const recent = [...members]
    .sort((a, b) => {
      const ta = a.joined_on?.seconds ?? 0;
      const tb = b.joined_on?.seconds ?? 0;
      return tb - ta;
    })
    .slice(0, 5);

  const loading = loadingMembers || loadingEvents;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Live overview of Team Fronx membership</p>
        </div>
      </div>

      {loading ? (
        <div className="dash-loading">
          <span className="tf-spinner tf-spinner--red" />
        </div>
      ) : (
        <>
          {/* ── Stat cards ───────────────────────────────────── */}
          <div className="dash-stats">
            <StatCard label="Total Members"    value={members.length} accent />
            <StatCard label="Active"           value={active}          color="green" />
            <StatCard label="Inactive"         value={inactive}        color="muted" />
            <StatCard label="Upcoming Events"  value={events.length}   color="amber" />
          </div>

          <div className="dash-grid">
            {/* ── District breakdown ───────────────────────── */}
            <section className="dash-card">
              <h2 className="dash-card-title">Members by District</h2>
              {topDistricts.length === 0 ? (
                <p className="dash-empty">No members yet.</p>
              ) : (
                <div className="dash-bars">
                  {topDistricts.map(([district, count]) => (
                    <div key={district} className="dash-bar-row">
                      <span className="dash-bar-label">{district}</span>
                      <div className="dash-bar-track">
                        <div
                          className="dash-bar-fill"
                          style={{ width: `${(count / maxCount) * 100}%` }}
                        />
                      </div>
                      <span className="dash-bar-count">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ── Recently added ───────────────────────────── */}
            <section className="dash-card">
              <h2 className="dash-card-title">Recently Added</h2>
              {recent.length === 0 ? (
                <p className="dash-empty">No members yet.</p>
              ) : (
                <ul className="dash-recent-list">
                  {recent.map(m => (
                    <li key={m.id} className="dash-recent-item">
                      <div className="dash-recent-avatar">
                        {m.name?.[0]?.toUpperCase() ?? '?'}
                      </div>
                      <div className="dash-recent-info">
                        <span className="dash-recent-name">{m.name}</span>
                        <span className="dash-recent-meta">
                          {m.district} · {m.car_variant}
                        </span>
                      </div>
                      <span className={`dash-status-chip dash-status-chip--${m.status}`}>
                        {m.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, color }) {
  const cls = accent ? 'dash-stat--accent'
            : color  ? `dash-stat--${color}`
            : '';
  return (
    <div className={`dash-stat ${cls}`}>
      <span className="dash-stat-value">{value}</span>
      <span className="dash-stat-label">{label}</span>
    </div>
  );
}
